import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageTitle, Th, Td, inputCls, labelCls, btnPrimary, btnGhost } from "@/admin/ui";

const EMPTY = { code: "", kind: "percent", value: "", max_redemptions: "", expires_at: "" };

function fmtDiscount(d) {
  if (d.percent_off) return `${d.percent_off}% off`;
  if (d.amount_off) return `$${d.amount_off.toFixed(2)} off`;
  return "—";
}

function fmtExpiry(ts) {
  if (!ts) return "Never";
  return new Date(ts * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function CreateForm({ onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const value = parseFloat(f.value);
    if (!f.code.trim() || !value || value <= 0) { toast.error("Enter a code and a value greater than 0."); return; }
    if (f.kind === "percent" && value > 100) { toast.error("Percent off cannot exceed 100."); return; }
    setBusy(true);
    try {
      const payload = {
        code: f.code.trim(),
        percent_off: f.kind === "percent" ? value : null,
        amount_off: f.kind === "amount" ? value : null,
        max_redemptions: f.max_redemptions ? parseInt(f.max_redemptions, 10) : null,
        expires_at: f.expires_at || null,
      };
      const { data } = await api.post("/admin/discounts", payload);
      toast.success(`Code ${data.code} is live — customers can use it at checkout.`);
      setF(EMPTY);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create the discount.");
    }
    setBusy(false);
  };

  return (
    <Card className="p-5 mb-6">
      <form onSubmit={submit} data-testid="discount-create-form">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-graphite mb-4">New discount code</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className={labelCls}>Code *</label>
            <input value={f.code} onChange={set("code")} className={`${inputCls} uppercase`}
              placeholder="SUMMER15" maxLength={20} required data-testid="discount-code-input" />
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <select value={f.kind} onChange={set("kind")} className={`${inputCls} bg-ink`} data-testid="discount-kind-select">
              <option value="percent">Percent off</option>
              <option value="amount">Dollar amount off</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>{f.kind === "percent" ? "Percent (%)" : "Amount (USD)"} *</label>
            <input type="number" step="0.01" min="0.01" max={f.kind === "percent" ? 100 : undefined}
              value={f.value} onChange={set("value")} className={inputCls}
              placeholder={f.kind === "percent" ? "15" : "10.00"} required data-testid="discount-value-input" />
          </div>
          <div>
            <label className={labelCls}>Max uses (optional)</label>
            <input type="number" min="1" value={f.max_redemptions} onChange={set("max_redemptions")}
              className={inputCls} placeholder="Unlimited" data-testid="discount-max-input" />
          </div>
          <div>
            <label className={labelCls}>Expires (optional)</label>
            <input type="date" value={f.expires_at} onChange={set("expires_at")}
              className={`${inputCls} [color-scheme:dark]`} data-testid="discount-expiry-input" />
          </div>
        </div>
        <button type="submit" disabled={busy} className={`${btnPrimary} mt-4`} data-testid="discount-create-btn">
          {busy ? "Creating…" : "Create Code"}
        </button>
        <p className="font-mono text-[10px] text-graphite mt-3">
          Codes apply once per order and work instantly in the "Add promotion code" field on Stripe checkout.
        </p>
      </form>
    </Card>
  );
}

export default function Discounts() {
  const [discounts, setDiscounts] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const load = () => api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch(() => setDiscounts([]));
  useEffect(() => { load(); }, []);

  const toggle = async (d) => {
    setTogglingId(d.id);
    try {
      await api.patch(`/admin/discounts/${d.id}`, { active: !d.active });
      toast.success(d.active ? `${d.code} deactivated.` : `${d.code} is active again.`);
      load();
    } catch { toast.error("Could not update the code."); }
    setTogglingId(null);
  };

  return (
    <div data-testid="admin-discounts-page">
      <PageTitle title="Discounts" sub="Create promo codes customers can redeem at checkout." />
      <CreateForm onCreated={load} />
      <Card className="overflow-x-auto">
        <table className="w-full min-w-[680px]">
          <thead className="border-b border-border">
            <tr><Th>Code</Th><Th>Discount</Th><Th>Used</Th><Th>Revenue</Th><Th>Disc. given</Th><Th>Expires</Th><Th>Status</Th><Th className="text-right">Action</Th></tr>
          </thead>
          <tbody>
            {discounts === null && (
              <tr><Td colSpan={8} className="text-graphite font-mono text-xs py-8 text-center">Loading…</Td></tr>
            )}
            {discounts?.length === 0 && (
              <tr><Td colSpan={8} className="text-graphite font-mono text-xs py-8 text-center">No discount codes yet — create your first one above.</Td></tr>
            )}
            {(discounts || []).map((d) => (
              <tr key={d.id} className="border-b border-border/40" data-testid={`discount-row-${d.code}`}>
                <Td className="font-mono text-xs tracking-[0.15em] text-signal">{d.code}</Td>
                <Td className="font-mono text-xs">{fmtDiscount(d)}</Td>
                <Td className="font-mono text-xs text-graphite">
                  {d.times_redeemed}{d.max_redemptions ? ` / ${d.max_redemptions}` : ""}
                </Td>
                <Td className="font-mono text-xs text-signal" data-testid={`discount-revenue-${d.code}`}>
                  ${(d.revenue || 0).toFixed(2)}
                </Td>
                <Td className="font-mono text-xs text-graphite">
                  ${(d.discount_given || 0).toFixed(2)}
                </Td>
                <Td className="font-mono text-xs text-graphite">{fmtExpiry(d.expires_at)}</Td>
                <Td>
                  <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 border ${
                    d.active ? "text-signal border-signal/40" : "text-graphite border-border"}`}>
                    {d.active ? "Active" : "Inactive"}
                  </span>
                </Td>
                <Td className="text-right">
                  <button onClick={() => toggle(d)} disabled={togglingId === d.id} data-testid={`discount-toggle-${d.code}`}
                    className={`font-mono text-[10px] uppercase tracking-wider border px-3 py-1.5 transition-colors disabled:opacity-50 ${
                      d.active ? "border-pulse/50 text-pulse hover:bg-pulse hover:text-ink" : "border-signal/50 text-signal hover:bg-signal hover:text-ink"}`}>
                    {d.active ? "Deactivate" : "Reactivate"}
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <p className="font-mono text-[10px] text-graphite mt-4">
        Deactivated codes stop working immediately but keep their redemption history. Codes cannot be deleted (Stripe rule) — deactivate instead.
        Revenue / discount figures cover orders paid after discount tracking was enabled.
      </p>
    </div>
  );
}
